import {
    EmployeeRequestService,
    type EmployeeRequestCreateTask,
} from '@api/EmployeeRequestService';
import { useCallback, useEffect, useState } from 'react';
import { useApiCall } from '../apiCall';
import { invalidateOrgEmployeeRequestData } from '@stores/orgEmployeeRequestStore';

interface UseEmployeeRequestsCreateResult {
    createdEmployeeRequests: unknown;
    isEmployeeRequestsCreationInProgress: boolean;
    createEmployeeRequests: () => void;
    error?: string | undefined;
}

/*
 * Creates employee requests across both scopes via useApiCall (see
 * EmployeeRequestService.createEmployeeRequests): org-scoped sources
 * are posted once to the org endpoint, while department-scoped sources
 * are posted per department.
 *
 * The tasks are snapshotted on submit: useApiCall re-fires whenever its
 * params change while a call is in flight, and the live task list would
 * change on every keystroke in the modal.
 */
const useEmployeeRequestsCreate = (
    orgId: string,
    createTasks: EmployeeRequestCreateTask[],
): UseEmployeeRequestsCreateResult => {
    const [submissionTasks, setSubmissionTasks] = useState<
        EmployeeRequestCreateTask[] | undefined
    >();

    const {
        response: createdEmployeeRequests,
        isLoading: isEmployeeRequestsCreationInProgress,
        error,
        requestApiCall,
    } = useApiCall(
        EmployeeRequestService.createEmployeeRequests,
        [orgId, submissionTasks],
        [orgId, submissionTasks],
        false,
    );

    const createEmployeeRequests = useCallback(() => {
        setSubmissionTasks(createTasks);
        requestApiCall();
    }, [createTasks, requestApiCall]);

    /*
     * Created requests invalidate the cached org employee request
     * data, so the cache never serves the pre-creation lists.
     */
    useEffect(() => {
        if (createdEmployeeRequests !== undefined) {
            invalidateOrgEmployeeRequestData();
        }
    }, [createdEmployeeRequests]);

    return {
        createdEmployeeRequests,
        isEmployeeRequestsCreationInProgress,
        createEmployeeRequests,
        error,
    };
};

export default useEmployeeRequestsCreate;
